import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { LocalTaxi, DirectionsCar, LocationOn } from '@mui/icons-material';
import apiService from '../services/apiService';
import socketService from '../services/socketService';

const LiveTrackingPage = () => {
  const [activeRides, setActiveRides] = useState([]);
  const [onlineDrivers, setOnlineDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLiveData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getLiveTracking();

      if (response.success) {
        setActiveRides(response.data.activeRides || []);
        setOnlineDrivers(response.data.onlineDrivers || []);
      } else {
        setError(response.message || 'Failed to load live data');
        setActiveRides([]);
        setOnlineDrivers([]);
      }
    } catch (err) {
      console.error('Live tracking error:', err);
      setError('Failed to load live data');
      setActiveRides([]);
      setOnlineDrivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveData();

    // Setup socket listeners
    socketService.onLocationUpdate((data) => {
      console.log('Location update:', data);
    });

    socketService.onRideUpdate(() => {
      fetchLiveData();
    });

    // Join live tracking room
    socketService.joinLiveTracking();

    return () => {
      socketService.leaveLiveTracking();
    };
  }, [fetchLiveData]);

  const getStatusColor = (status) => {
    const colors = {
      searching_driver: 'warning',
      driver_assigned: 'info',
      driver_arrived: 'info',
      in_progress: 'success',
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Live Tracking
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Real-time tracking of active rides and online drivers
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h3" fontWeight="bold" color="success.main">
              {activeRides.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Rides
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h3" fontWeight="bold" color="primary.main">
              {onlineDrivers.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Online Drivers
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Active Rides */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocalTaxi color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Active Rides
              </Typography>
            </Box>
            {activeRides.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocalTaxi sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No active rides at the moment
                </Typography>
              </Box>
            ) : (
              <List>
                {activeRides.map((ride) => (
                  <ListItem key={ride.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <LocalTaxi />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {ride.id?.substring(0, 12)}...
                          </Typography>
                          <Chip label={ride.status?.replace('_', ' ')} color={getStatusColor(ride.status)} size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            Passenger: {ride.passenger || '-'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Driver: {ride.driver || 'Not assigned'} {ride.vehicleType ? `(${ride.vehicleType})` : ''}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {ride.pickupLocation || 'N/A'} → {ride.dropLocation || 'N/A'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Online Drivers */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DirectionsCar color="success" />
              <Typography variant="h6" fontWeight={600}>
                Online Drivers
              </Typography>
            </Box>
            {onlineDrivers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DirectionsCar sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No drivers online at the moment
                </Typography>
              </Box>
            ) : (
              <List>
                {onlineDrivers.map((driver) => (
                  <ListItem key={driver.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <DirectionsCar />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={driver.name || 'Unknown Driver'}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {driver.vehicleType || 'N/A'} {driver.vehicleNumber ? `- ${driver.vehicleNumber}` : ''}
                          </Typography>
                          <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                            <LocationOn sx={{ fontSize: 14 }} />
                            {driver.currentLocation || 'Location unavailable'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Note:</strong> This page shows real-time data. The map integration with Google Maps will be added in future updates.
      </Alert>
    </Box>
  );
};

export default LiveTrackingPage;
